import { fetchEntryAttachments, removeEntryAttachment } from '@/api/resources/entryAttachment.api';
import { ENTRY_ATTACHMENT_STALE_TIME_MS } from '@/constants/file.constants';
import { invalidateObservationTimelineQueries } from '@/composables/observationTimeline/observationTimelineQueryInvalidation';
import { invalidateSupportMeasureAttachmentDetails } from '@/composables/supportMeasureEntry/supportMeasureEntryQueryInvalidation';
import { useInfiniteQueryWithToast } from '@/composables/useQueryWithToast';
import { useToast } from '@/composables/useToast';
import { useWhoAmI } from '@/composables/useWhoAmI';
import { useQueryClient } from '@tanstack/vue-query';
import type { IEntryAttachmentTarget } from 'common/enum/entry.attachment.enum';
import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue';
import { entryAttachmentKeys } from './entryAttachment.keys';
import { invalidateEntryAttachmentQueries } from './entryAttachmentQueryInvalidation';

export function useEntryAttachments(
  target: MaybeRefOrGetter<IEntryAttachmentTarget>,
  enabled: MaybeRefOrGetter<boolean>,
) {
  const { currentTenant, currentUserRole, whoAmI } = useWhoAmI();
  const queryClient = useQueryClient();
  const { showError } = useToast();
  const saving = ref(false);
  const queryKey = computed(() =>
    entryAttachmentKeys.list(toValue(target), [currentTenant.value?.id, whoAmI.value?.id, currentUserRole.value?.id]),
  );
  const pagesQuery = useInfiniteQueryWithToast({
    queryKey,
    queryFn: ({ pageParam }) => fetchEntryAttachments(toValue(target), pageParam || undefined),
    initialPageParam: 0,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: computed(() => toValue(enabled) && toValue(target).entryId > 0),
    staleTime: ENTRY_ATTACHMENT_STALE_TIME_MS,
    meta: { showErrorToast: false },
  });
  const query = {
    ...pagesQuery,
    data: computed(() => {
      const pages = pagesQuery.data.value?.pages;
      const first = pages?.[0];
      if (!first) return undefined;
      return { ...first, items: pages.flatMap((page) => page.items) };
    }),
  };

  const refresh = async (): Promise<void> => {
    const changed = toValue(target);
    await invalidateEntryAttachmentQueries(queryClient, changed, { throwOnError: true });
    await invalidateObservationTimelineQueries(queryClient, query.data.value?.schoolClassId);
    await invalidateSupportMeasureAttachmentDetails(queryClient, changed);
  };

  const mutate = async (action: () => Promise<void>): Promise<void> => {
    if (saving.value) return;
    saving.value = true;
    try {
      await action();
      await refresh();
    } catch (error) {
      showError(error);
    } finally {
      saving.value = false;
    }
  };

  return {
    query,
    saving,
    refresh,
    removeFile: (id: number) => mutate(() => removeEntryAttachment(toValue(target), id)),
  };
}
